import moment from 'moment-timezone';
import jwtDecode from 'jwt-decode';
import config from '../../../config';
import {authFetch, checkStatus} from '../../utils/http';
import {BACKOFFICE_ROLE, MANAGER_ROLE} from '../../utils/constant';
import {consoleLog} from '../../utils/log';

export default class RolesAdapter {
  /**
   * The role per organisation structure
   *
   * @typedef UserOrganisationPermissionDTO
   * @property {string} userId
   * @property {number} organisationId
   * @property {string} role
   */

  /**
   * The roles token contains the next payload
   *
   * @typedef {Object} Payload
   * @property {string} userId
   * @property {string} role
   * @property {Object} expirationDate
   * @property {UserOrganisationPermissionDTO} userOrganisationPermission
   */

  constructor(store) {
    this.store = store;

    /** @type Payload */
    this.payload = {
      // The expirationDate is set to be already expired
      expirationDate: moment().add(-1, 'y')
    };
    this.getGlobalRole = this.getGlobalRole.bind(this);
    this.getOrganisationRole = this.getOrganisationRole.bind(this);
  }

  /**
   * update the token if expired or if the organisation has changed, then return the adapter
   * @param {number} userId
   * @param {number} minValidity the minimum validity required before update in seconds
   */
  updateToken(userId, minValidity) {
    function decodeToken() {
      /** @type Payload */
      const payloadJWT = jwtDecode(this.token);

      // timeSkew is the estimated difference between server and client in millisecond
      const timeSkew = moment().diff(moment(payloadJWT.issuedAtTime));
      consoleLog(`[ROLES] Estimated time difference between browser and server is ${timeSkew} ms`);

      /** @type Payload */
      this.payload = {
        userId: payloadJWT.userId,
        role: payloadJWT.role,
        expirationDate: moment(payloadJWT.expirationDate),
        userOrganisationPermission: payloadJWT.userOrganisationPermission,
        timeSkew,
        email: payloadJWT.email,
        firstName: payloadJWT.firstName,
        lastName: payloadJWT.lastName,
        fullName: payloadJWT.fullName,
        language: payloadJWT.language,
        phoneNumber: payloadJWT.phoneNumber,
        entities: payloadJWT.entities,
        generalTermsAgreementTimestamp: payloadJWT.generalTermsAgreementTimestamp,
        privacyPolicyAgreementTimestamp: payloadJWT.privacyPolicyAgreementTimestamp
      };
    }

    function getOrganisationId() {
      const {organisation} = this.store.getState();
      return organisation ? organisation.organisationId : null;
    }

    function shouldFetchToken() {
      /** @type Payload */
      const {payload} = this;

      // If is expired...
      if (payload.expirationDate.isBefore(
        moment()
          .add(minValidity, 's')
          .add(payload.timeSkew, 'ms')
      )) {
        return true;
      }

      // ... Or if the organisation context has changed
      const tokenOrganisationId = payload.userOrganisationPermission ? payload.userOrganisationPermission.organisationId : null;
      return getOrganisationId.call(this) !== tokenOrganisationId;
    }

    function fetchToken() {
      const organisationId = getOrganisationId.call(this);

      let url = `${config.userApi}/api/v1/users/permission/${userId}`;
      if (organisationId) {
        url += `/${organisationId}`;
      }

      if (this.getGlobalRole() === BACKOFFICE_ROLE) {
        const stored = localStorage.getItem('backOfficeRole');
        const forcedRole = stored || MANAGER_ROLE;
        url += `?withRole=${forcedRole}`;
      }

      return authFetch(url, {method: 'GET'}, true)
        .then(checkStatus)
        .then(response => response.json());
    }

    return new Promise((resolve, reject) => {
      if (shouldFetchToken.call(this)) {
        fetchToken.call(this).then(response => {
          this.token = response.token;
          decodeToken.call(this);
          resolve(this);
        }).catch(reject);
      } else {
        resolve(this);
      }
    });
  }

  /**
   * Return the raw token fetch from the server
   */
  getToken() {
    return this.token;
  }

  /**
   * Return the payload of the token
   * @return Payload
   */
  getPayload() {
    return this.payload;
  }

  /**
   * Return global role of the user (DEFAULT, BACK_OFICE, ...)
   * @return {string}
   */
  getGlobalRole() {
    return this.payload.role;
  }

  /**
  * Return organisation role or null if there is no organisation (DEFAULT, MANAGER, ...)
  * @return {string|null}
  */
  getOrganisationRole() {
    return this.payload.userOrganisationPermission ? this.payload.userOrganisationPermission.role : null;
  }

  /**
   * Return organisation permissions or [] is there is no permission
   */
  getOrganisationPermissions() {
    return this.payload.userOrganisationPermission && this.payload.userOrganisationPermission.permissions ?
      this.payload.userOrganisationPermission.permissions : [];
  }
}
